import { ia, iaRa } from 'lib/helpers/utility';

import { CHART_HOVER_MARKER_SIZE } from './constants';

const SYNTHETIC_MARK = '\u0000';
const SYNTHETIC_KEY = '__smoothSynthetic';
const ANCHOR_KEY = '__smoothAnchor';
const SMOOTHABLE_TYPES = new Set(['area', 'line']);
const MIN_ROWS = 3;

const stepsFor = (count) => {
  if (count > 120) return 3;
  if (count > 90) return 5;
  if (count > 45) return 9;
  return 14;
};

const toNumericX = (value) => (value instanceof Date ? value.getTime() : Number(value));

const fromNumericX = (sample, value) => (sample instanceof Date ? new Date(value) : value);

const cardinalTangents = (xs, ys) => {
  const last = xs.length - 1;
  const tangents = new Array(xs.length);
  const slope = (from, to) => {
    if (xs[to] === xs[from] || !Number.isFinite(ys[from]) || !Number.isFinite(ys[to])) return NaN;
    return (ys[to] - ys[from]) / (xs[to] - xs[from]);
  };
  const pick = (...candidates) => candidates.find((value) => Number.isFinite(value)) ?? 0;

  tangents[0] = pick(slope(0, 1));
  tangents[last] = pick(slope(last - 1, last));

  for (let index = 1; index < last; index++) {
    tangents[index] = pick(
      slope(index - 1, index + 1),
      slope(index - 1, index),
      slope(index, index + 1)
    );
  }

  return tangents;
};

const hermite = ({ ratio, width, y0, y1, t0, t1, floor }) => {
  if (!Number.isFinite(y0) || !Number.isFinite(y1)) return NaN;

  const squared = ratio * ratio;
  const cubed = squared * ratio;
  const value =
    y0 * (2 * cubed - 3 * squared + 1) +
    t0 * width * (cubed - 2 * squared + ratio) +
    y1 * (-2 * cubed + 3 * squared) +
    t1 * width * (cubed - squared);

  return Math.max(value, floor);
};

const smoothRows = ({ rows, xKey, yKeys, isCategory }) => {
  const xs = rows.map((row, index) => (isCategory ? index : toNumericX(row?.[xKey])));

  if (xs.some((x) => !Number.isFinite(x))) return null;
  if (xs.some((x, index) => index > 0 && x <= xs[index - 1])) return null;

  const values = {};
  const tangents = {};
  const floors = {};

  for (const key of yKeys) {
    const series = rows.map((row) => {
      const value = row?.[key];
      return value === null || value === '' ? NaN : Number(value);
    });

    values[key] = series;
    tangents[key] = cardinalTangents(xs, series);
    floors[key] = series.reduce(
      (min, value) => (Number.isFinite(value) ? Math.min(min, value) : min),
      0
    );
  }

  const steps = stepsFor(rows.length);
  const smoothed = [];

  rows.forEach((row, index) => {
    smoothed.push({ ...row, [ANCHOR_KEY]: row });
    if (index === rows.length - 1) return;

    const width = xs[index + 1] - xs[index];

    for (let step = 1; step < steps; step++) {
      const ratio = step / steps;
      const point = {
        [SYNTHETIC_KEY]: true,
        [ANCHOR_KEY]: ratio < 0.5 ? row : rows[index + 1]
      };

      point[xKey] = isCategory
        ? `${String(row?.[xKey])}${SYNTHETIC_MARK}${step}`
        : fromNumericX(row?.[xKey], xs[index] + width * ratio);

      yKeys.forEach((key) => {
        point[key] = hermite({
          ratio,
          width,
          y0: values[key][index],
          y1: values[key][index + 1],
          t0: tangents[key][index],
          t1: tangents[key][index + 1],
          floor: floors[key]
        });
      });

      smoothed.push(point);
    }
  });

  return smoothed;
};

const anchorParams = (params) => {
  const anchor = params?.datum?.[ANCHOR_KEY];
  if (!anchor) return params;

  return {
    ...params,
    datum: anchor,
    xValue: anchor[params?.xKey],
    yValue: anchor[params?.yKey]
  };
};

const smoothSeries = (item) => ({
  ...item,
  marker: {
    ...(item?.marker || {}),
    formatter: (params) =>
      params?.datum?.[SYNTHETIC_KEY]
        ? { size: 0 }
        : { size: params?.highlighted ? CHART_HOVER_MARKER_SIZE : params?.size }
  },
  ...(typeof item?.tooltip?.renderer === 'function' && {
    tooltip: { ...item.tooltip, renderer: (params) => item.tooltip.renderer(anchorParams(params)) }
  })
});

const hideSyntheticLabels = (axis) => {
  const formatter = axis?.label?.formatter;

  return {
    ...axis,
    label: {
      ...(axis?.label || {}),
      formatter: (params) => {
        const value = params?.value;
        if (typeof value === 'string' && value.includes(SYNTHETIC_MARK)) return '';
        return formatter ? formatter(params) : value;
      }
    }
  };
};

export const applySmoothing = (options = {}) => {
  const { smooth, ...chartOptions } = options;
  const series = iaRa(chartOptions.series);

  if (
    smooth === false ||
    !ia(series) ||
    !series.every((item) => SMOOTHABLE_TYPES.has(item?.type))
  ) {
    return chartOptions;
  }

  const xKey = series[0]?.xKey;
  if (!xKey || series.some((item) => item?.xKey !== xKey)) return chartOptions;

  const yKeys = [...new Set(series.map((item) => item?.yKey).filter(Boolean))];
  const rows = iaRa(chartOptions.data);
  if (!ia(yKeys) || rows.length < MIN_ROWS) return chartOptions;

  const bottomAxis = iaRa(chartOptions.axes).find((axis) => axis?.position === 'bottom');
  const axisType = bottomAxis?.type;
  const hasTextX = typeof rows[0]?.[xKey] === 'string';
  if (hasTextX && axisType && axisType !== 'category') return chartOptions;

  const isCategory = axisType ? axisType === 'category' : hasTextX;
  if (isCategory && !bottomAxis) return chartOptions;

  const data = smoothRows({ rows, xKey, yKeys, isCategory });
  if (!data) return chartOptions;

  return {
    ...chartOptions,
    data,
    series: series.map(smoothSeries),
    ...(isCategory && {
      axes: iaRa(chartOptions.axes).map((axis) =>
        axis === bottomAxis ? hideSyntheticLabels(axis) : axis
      )
    })
  };
};
